import { Box, Modal, Typography } from "@mui/material";
import { format, getDay, parse, startOfWeek } from "date-fns";
import moment from "moment";
import { useEffect, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../components/dynamicTable";
import { getJobs } from "../../services/jobs";
import "./index.css";
import AppLoader from "../../components/loader/loader";

const locales = { "en-US": require("date-fns/locale/en-US") };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const URGENCY_COLORS = {
  Cancelled: "#F80D38",
  Booked: "#FECA57",
  Pending: "#FFA500",
  Completed: "#4CAF50",
};
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  overflowY: "auto",
  borderRadius: 2,
};

const tableHeader = [
  "JOB ID",
  "CUSTOMER NAME",
  "JOBS",
  "ADDRESS",
  "CREATED AT",
  "LOCATION",
  "STATUS",
  "ACTIONS",
];

const displayRows = [
  "jobId",
  "customer_details",
  "jobs_details",
  "address",
  "created_at",
  "location",
  "jobStatus",
  "eye",
];

const GroupJob = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [jobsData, setJobsData] = useState([]);
  const [selectedDateJobs, setSelectedDateJobs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllJobs();
  }, []);

  const fetchAllJobs = async () => {
    try {
      setLoading(true);
      const response = await getJobs();
      if (response.data.status === "success") {
        setJobsData(response.data.data);
      } else {
        console.error("Fetch error:", response.data?.message);
      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setLoading(false);
    }
  };

  const calendarEvents = jobsData.flatMap((job) =>
    job.job.map((j, index) => ({
      id: `${job._id}-${index}`,
      title: job.jobStatus || "Pending",
      start: moment.utc(j.startDate).local().toDate(),
      end: moment.utc(j.endDate).local().toDate(),
      status: job.jobStatus || "Pending",
      fullJobData: job,
    }))
  );

  const handleEventClick = (event, e) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedDate = moment(event.start).format("YYYY-MM-DD");
    const matchedJobs = jobsData.filter((job) =>
      job.job.some(
        (j) => moment(j.startDate).format("YYYY-MM-DD") === selectedDate
      )
    );
    setSelectedDateJobs(matchedJobs);
    setShowModal(true);
  };

  const handleMoreClick = (date) => {
    const selectedDate = moment(date).format("YYYY-MM-DD");
    const matchedJobs = jobsData.filter((job) =>
      job.job.some(
        (j) => moment(j.startDate).format("YYYY-MM-DD") === selectedDate
      )
    );
    setSelectedDateJobs(matchedJobs);
    setShowModal(true);
  };

  const handleJobDetails = (row) => {
    navigate(`/jobs-details/${row?._id}`, { state: row });
  };

  const eventStyleGetter = (event) => {
    const backgroundColor = URGENCY_COLORS[event.status] || "#64B166";
    return {
      style: {
        backgroundColor,
        borderRadius: "8px",
        color: "#fff",
        fontSize: "13px",
        fontWeight: 500,
        padding: "2px 6px",
        textAlign: "center",
      },
    };
  };

  const CustomDateCellWrapper = ({ value }) => {
    const dayEvents = calendarEvents.filter(
      (e) =>
        moment(e.start).format("YYYY-MM-DD") ===
        moment(value).format("YYYY-MM-DD")
    );

    const maxVisible = 2;
    const visibleEvents = dayEvents.slice(0, maxVisible);
    const extraCount = dayEvents.length - maxVisible;

    return (
      <div style={{ padding: "4px", position: "relative", minHeight: 60 }}>
        {visibleEvents.map((event, idx) => (
          <div
            key={event.id}
            onClick={(e) => handleEventClick(event, e)}
            style={{
              ...eventStyleGetter(event).style,
              cursor: "pointer",
              marginBottom: 2,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {event.title}
          </div>
        ))}
        {extraCount > 0 && (
          <div
            style={{
              color: "#007bff",
              fontSize: "12px",
              cursor: "pointer",
              marginTop: 4,
            }}
            onClick={() => handleMoreClick(value)}
          >
            +{extraCount} more
          </div>
        )}
      </div>
    );
  };

  return (
    <Box sx={{ height: "100vh", bgcolor: "#fff", p: 2 }}>
      <Calendar
        localizer={localizer}
        events={calendarEvents}
        date={date}
        onNavigate={setDate}
        views={["month"]}
        defaultView="month"
        style={{ height: 700 }}
        popup // show +X more modal
        onSelectEvent={(event) => {
          const selectedDate = moment(event.start).format("YYYY-MM-DD");
          const matchedJobs = jobsData.filter((job) =>
            job.job.some(
              (j) => moment(j.startDate).format("YYYY-MM-DD") === selectedDate
            )
          );
          setSelectedDateJobs(matchedJobs);
          setShowModal(true);
        }}
        eventPropGetter={(event) => {
          const backgroundColor = URGENCY_COLORS[event.status] || "#64B166";
          return {
            style: {
              backgroundColor,
              borderRadius: "8px",
              color: "#fff",
              fontSize: "13px",
              fontWeight: 500,
              padding: "2px 6px",
              textAlign: "center",
            },
          };
        }}
      />

      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" gutterBottom>
            Jobs on Selected Date
          </Typography>
          <DynamicTable
            tableHeader={tableHeader}
            displayRows={displayRows}
            tableData={selectedDateJobs}
            showPagination={true}
            showUserMenu={true}
            onEyeClick={handleJobDetails}
          />
        </Box>
      </Modal>

      <Box display="flex" gap={2} mt={3}>
        {[
          { color: "#FF5B5B", label: "Cancelled Booking" },
          { color: "#FFA500", label: "Pending" },
          { color: "#32CD32", label: "Available Booking" },
        ].map((item) => (
          <Box key={item.label} display="flex" alignItems="center" gap={1}>
            <Box
              width={12}
              height={12}
              borderRadius="50%"
              bgcolor={item.color}
            />
            <Typography variant="body2">{item.label}</Typography>
          </Box>
        ))}
      </Box>

      <AppLoader isVisible={loading} />
    </Box>
  );
};

export default GroupJob;
